import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionHeader } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Approvals() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [defaultApprover, setDefaultApprover] = useState("");
  const [defaultThreshold, setDefaultThreshold] = useState(0);
  const [deptRules, setDeptRules] = useState([]);

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: user?.role === "admin",
  });
  const { data: settings } = useQuery({
    queryKey: ["approval-settings"],
    queryFn: async () => (await api.get("/approval-settings")).data,
    enabled: user?.role === "admin",
  });
  const { data: trendData } = useQuery({
    queryKey: ["approval-trends"],
    queryFn: async () => (await api.get("/approval-trends")).data,
    enabled: user?.role === "admin",
  });
  const trends = trendData?.trends || [];
  const hasTrend = trends.some((t) => t.approved || t.declined);

  useEffect(() => {
    if (settings) {
      setEnabled(!!settings.enabled);
      setDefaultApprover(settings.default_approver_user_id || "");
      setDefaultThreshold(settings.default_threshold || 0);
      setDeptRules((settings.dept_rules || []).map((r) => ({ department: r.department || "", approver_user_id: r.approver_user_id || "", threshold: r.threshold || 0 })));
    }
  }, [settings]);

  const usersWithEmail = users.filter((u) => u.email);
  const userLabel = (u) => `${u.name || u.email}${u.email ? ` · ${u.email}` : ""}`;

  const save = useMutation({
    mutationFn: async () => (await api.put("/approval-settings", {
      enabled,
      default_approver_user_id: defaultApprover,
      default_threshold: Number(defaultThreshold || 0),
      dept_rules: deptRules.filter((r) => r.department.trim() && r.approver_user_id).map((r) => ({ ...r, threshold: Number(r.threshold || 0) })),
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["approval-settings"] }); toast.success("Approval rules saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const addRule = () => setDeptRules((rs) => [...rs, { department: "", approver_user_id: "", threshold: 0 }]);
  const setRule = (i, k, v) => setDeptRules((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRule = (i) => setDeptRules((rs) => rs.filter((_, idx) => idx !== i));

  if (user?.role !== "admin") {
    return (
      <div className="animate-fade-up border border-border bg-card p-10 text-center">
        <ShieldCheck size={40} className="mx-auto text-muted-foreground mb-3" />
        <h2 className="text-lg font-bold">Administrators only</h2>
        <p className="text-sm text-muted-foreground mt-1">You need the admin role to manage approval rules.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-up max-w-3xl" data-testid="approvals-page">
      <SectionHeader title="Approvals" subtitle="Purchase order approval workflow" />

      <div className="border border-border bg-card rounded-sm p-5 mb-5" data-testid="approval-trend-card">
        <div className="flex items-center justify-between mb-3">
          <p className="overline-label text-primary">Approvals Trend</p>
          <p className="text-xs text-muted-foreground">Last 6 months · volume &amp; avg approval time</p>
        </div>
        {hasTrend ? (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={trends} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" unit="h" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} formatter={(v, n) => n === "Avg hours" ? [`${v}h`, n] : [v, n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="approved" name="Approved" fill="#16A34A" radius={[2, 2, 0, 0]} maxBarSize={28} />
              <Bar yAxisId="left" dataKey="declined" name="Declined" fill="#DC2626" radius={[2, 2, 0, 0]} maxBarSize={28} />
              <Line yAxisId="right" type="monotone" dataKey="avg_hours" name="Avg hours" stroke="#F5720D" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground py-10 text-center" data-testid="approval-trend-empty">No approval decisions yet. The chart populates as purchase orders are approved or declined.</p>
        )}
      </div>


      <div className="border border-border bg-card rounded-sm p-5 space-y-6">
        {/* Enable toggle */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="text-sm font-semibold">Require approval for purchase orders</Label>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md">When on, every purchase order must be approved before it can be received, invoiced or sent to the vendor.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="approval-enabled-switch" />
        </div>

        <div className="h-px bg-border" />

        {/* Default approver */}
        <div>
          <Label className="overline-label">Default approver</Label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">This user approves all purchase orders unless a department rule below applies. They must have an email on their account.</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
            <Select value={defaultApprover} onValueChange={setDefaultApprover}>
              <SelectTrigger className="rounded-sm flex-1" data-testid="default-approver-select"><SelectValue placeholder="Select a user…" /></SelectTrigger>
              <SelectContent>
                {usersWithEmail.map((u) => <SelectItem key={u.user_id} value={u.user_id} data-testid={`approver-option-${u.user_id}`}>{userLabel(u)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="sm:w-56">
              <Input type="number" min="0" step="0.01" value={defaultThreshold} onChange={(e) => setDefaultThreshold(e.target.value)} className="rounded-sm" placeholder="Approval threshold ($)" data-testid="default-threshold-input" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">Approval threshold: orders at or below this amount skip approval. Set 0 to require approval for every order.</p>
        </div>

        <div className="h-px bg-border" />

        {/* Department rules */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="overline-label">Department rules (optional)</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Route approvals to a specific person based on the purchase order's department.</p>
            </div>
            <Button variant="outline" onClick={addRule} className="rounded-sm h-8 text-xs" data-testid="add-dept-rule"><Plus size={14} className="mr-1" /> Add rule</Button>
          </div>
          {deptRules.length === 0 && <p className="text-sm text-muted-foreground py-3">No department rules. The default approver handles all purchase orders.</p>}
          <div className="space-y-2">
            {deptRules.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`dept-rule-${i}`}>
                <Input
                  value={r.department}
                  onChange={(e) => setRule(i, "department", e.target.value)}
                  placeholder="Department (e.g. IT)"
                  className="rounded-sm flex-1"
                  data-testid={`dept-rule-name-${i}`}
                />
                <Select value={r.approver_user_id} onValueChange={(v) => setRule(i, "approver_user_id", v)}>
                  <SelectTrigger className="rounded-sm flex-1" data-testid={`dept-rule-approver-${i}`}><SelectValue placeholder="Approver…" /></SelectTrigger>
                  <SelectContent>
                    {usersWithEmail.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{userLabel(u)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" min="0" step="0.01" value={r.threshold} onChange={(e) => setRule(i, "threshold", e.target.value)} placeholder="Threshold ($)" className="rounded-sm w-32" data-testid={`dept-rule-threshold-${i}`} />
                <button onClick={() => removeRule(i)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`remove-dept-rule-${i}`}><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="h-px bg-border" />

        <div className="flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="save-approval-settings">
            <CheckCircle size={16} weight="bold" className="mr-1.5" /> {save.isPending ? "Saving…" : "Save Rules"}
          </Button>
        </div>
      </div>
    </div>
  );
}
